// The assistant chooses the task; an idle conversation has no editor.
const SUPERPLUMMA_WORKSPACE_ROUTES = ['innboks', 'nyttkjop', 'annetsalg', 'faktura'];
function useSuperPlummaWorkspaceRoute(route, setRoute) {
  const [workspaceRoute, setWorkspaceRoute] = useState('');
  const current = useRef({ route, setRoute });
  current.current = { route, setRoute };
  const navigate = useCallback((next) => {
    if (current.current.route === 'superplumma' && SUPERPLUMMA_WORKSPACE_ROUTES.includes(next)) setWorkspaceRoute(next);
    else current.current.setRoute(next);
  }, []);
  const reset = useCallback(() => setWorkspaceRoute(''), []);
  return { workspaceRoute, navigate, reset, effectiveRoute: route === 'superplumma' ? (workspaceRoute || 'hjem') : route };
}

function SuperPlummaWorkspacePage({ year }) {
  const control = React.useContext(SuperPlummaContext);
  const [view, setView] = useState('chat');
  const [loadedRoute, setLoadedRoute] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null);
  const fileInput = useRef(null), cameraInput = useRef(null), uploadLock = useRef(false);
  const mounted = useRef(true);
  const route = control?.workspace.workspaceRoute || '';
  const busy = Boolean(control?.state.busy || uploading);
  useEffect(() => () => { mounted.current = false; }, []);
  useEffect(() => {
    if (!route) return;
    let cancelled = false;
    setLoadError(false);
    window.PlummePageLoader.loadRoute(route).then(() => {
      if (!cancelled) setLoadedRoute(route);
    }).catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [route, retry]);
  // Only opening a task changes the compact view; a normal question stays in chat.
  useEffect(() => { if (route) setView('workspace'); }, [route, control?.state.approval?.id]);
  const upload = async (files) => {
    if (uploadLock.current || control.state.busy) return;
    uploadLock.current = true; setUploading(true); setUploadError('');
    const backend = window.PlummeBackend, businessId = backend.activeBusinessId, activeYear = backend.appState?.activeYear;
    const current = () => mounted.current && backend.activeBusinessId === businessId && backend.appState?.activeYear === activeYear;
    try {
      await window.PlummePageLoader.loadRoute('nyttkjop');
      if (!current()) return;
      await uploadPurchaseReceiptFiles(files, { isCurrent: current,
        onReceipt: entry => { if (current()) { setReceipt(entry); control.workspace.navigate('nyttkjop'); setView('workspace'); } },
        onError: message => { if (current()) setUploadError(message); },
      });
    } catch (error) { if (current()) setUploadError(error.message || t('spw.uploadError')); }
    finally { uploadLock.current = false; if (mounted.current) setUploading(false); }
  };
  const selectFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.length || busy || uploadLock.current) return;
    if (route) setPendingFiles(list); else void upload(list);
  };
  const resetWorkspace = () => {
    control.workspace.reset();
    setView('chat'); setLoadedRoute(''); setLoadError(false); setRetry(0);
    setReceipt(null); setUploadError(''); setDragging(false); setPendingFiles(null);
    if (fileInput.current) fileInput.current.value = '';
    if (cameraInput.current) cameraInput.current.value = '';
  };
  if (!control) return <Card><p>{t('spw.unavailable')}</p></Card>;
  const hasFiles = event => Array.from(event.dataTransfer?.types || []).includes('Files');
  return <div className={`superplumma-workspace ${route ? 'has-document' : 'chat-only'} view-${view}${dragging ? ' is-dragging' : ''}`}
    onDragOver={event => { if (hasFiles(event)) { event.preventDefault(); setDragging(true); } }}
    onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
    onDrop={event => { if (hasFiles(event)) { event.preventDefault(); setDragging(false); selectFiles(event.dataTransfer.files); } }}>
    {route && <header className="spw-header" data-assistant-exclude="true">
      <div className="spw-switch"><Segmented options={[
        { value: 'chat', label: t('spw.chat') }, { value: 'workspace', label: t('spw.workspace') },
      ]} value={view} onChange={setView} /></div>
    </header>}
    <div className="spw-layout">
      <section className="spw-chat" aria-label={t('spw.chat')} data-assistant-exclude="true">
        <SuperPlummaChat session={control.session} state={control.state} disabled={uploading} allowExpand={false} onReset={resetWorkspace} intro={t('spw.intro')} accessory={<div className="spw-attachments">
          <Button variant="ghost" size="sm" icon="upload" disabled={busy} onClick={() => fileInput.current.click()}>{t('spw.upload')}</Button>
          <Button className="spw-camera" variant="ghost" size="sm" icon="camera" disabled={busy} onClick={() => cameraInput.current.click()}>{t('spw.camera')}</Button>
          <span className="muted t-xs">{t('spw.dropHint')}</span>
          {uploading && <span role="status">{t('spw.uploading')}</span>}
          {uploadError && <span role="alert" className="invoice-control-error">{uploadError}</span>}
        </div>} />
      </section>
      {route && <section className="spw-work" aria-label={t('spw.workspace')}>
        {loadError ? <div role="alert"><p>{t('spw.loadError')}</p><Button onClick={() => setRetry(value => value + 1)}>{t('spw.retry')}</Button></div>
          : loadedRoute !== route ? <div className="spw-loading" role="status">{t('spw.loading')}</div>
          : route === 'faktura' ? <SuperPlummaInvoiceSurface control={control}/>
          : route === 'nyttkjop' ? <SuperPlummaPurchaseSurface key={receipt?.id || 'purchase'} receipt={receipt} onReceipt={setReceipt} control={control}/>
          : <SuperPlummaTaskDialog route={route} year={year} control={control}/>}
      </section>}
    </div>
    <input ref={fileInput} type="file" accept="image/*,application/pdf" multiple hidden onChange={event => { selectFiles(event.target.files); event.target.value = ''; }}/>
    <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden onChange={event => { selectFiles(event.target.files); event.target.value = ''; }}/>
    {dragging && <div className="spw-drop-cue" role="status"><Icon name="upload" size={32}/><b>{t('spw.drop')}</b></div>}
    {pendingFiles && <ConfirmModal title={t('spw.changeTitle')} message={t('spw.changeText')} danger={false} confirmLabel={t('spw.changeConfirm')} onClose={() => setPendingFiles(null)} onConfirm={() => { const files = pendingFiles; setPendingFiles(null); void upload(files); }}/>}
  </div>;
}
Object.assign(window, { SuperPlummaWorkspacePage });
